import {
  AssignmentStatusEnum,
  AssignmentViewModel,
  DayIncompleteReasonEnum,
  GetBookedAssignmentsForUserAndDateList,
  GlobalSettingEnum,
  GpsPause,
} from 'api/model';
import { useAssignmentGetGpsPausesForUserAndDate } from 'api/assignment/assignment';
import Checkbox from 'components/inputs/Checkbox';
import { useGoogleMapsContext } from 'components/map/GoogleMapsContext';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { formatTime } from 'utils/date-helpers';
import useGlobalSettings from 'hooks/useGlobalSettings';
import useDayDrivingTimes from './useDayDrivingTimes';
import { classifyGaps, Gap } from './gap-helper';
import hoveredDayAssignmentIdAtom from './hoveredAssignmentAtom';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.h1`
  margin: 0;
  padding: 0 25px 8px 25px;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 540px;
`;

const Legend = styled.div`
  padding: 4px 25px 8px 25px;
  font-size: 12px;
  color: #555;
`;

// Compact, scannable legend for the GPS layer: a short checkbox label on its own
// row, then the colour key as wrapping "swatch + text" chips so nothing gets crushed.
const LayerLegend = styled.div`
  padding: 0 25px 8px 25px;
  font-size: 12px;
  color: #555;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LegendKey = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

const Swatch = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  font-weight: bold;
`;

const SWEDEN_CENTER = { lat: 62.5, lng: 16.5 };

const COLOR_OK = '#1d4ed8';
const COLOR_BAD = '#d33';
const COLOR_HOVER = '#EA580C'; // orange — hovered planned route
const COLOR_TRACK = '#64748b'; // slate — the actual driven GPS track
const COLOR_TRACK_HOVER = '#0d9488'; // teal — hovered GPS track (distinct from the orange planned-route hover)
const COLOR_PAUSE = '#7c3aed'; // violet — neutral stationary pause when its assignment is hovered
const COLOR_PAUSE_MUTED = '#9ca3af'; // grey — neutral pause at rest (de-emphasised until its assignment is hovered)
const COLOR_PAUSE_BREAK = '#15803d'; // green — pause that on its own fulfils the assignment's set break time (always shown)
const COLOR_GAP = '#475569'; // slate — gap where no break is required yet (neutral)
const COLOR_GAP_SHORT = '#ca8a04'; // amber — break was required here but the gap was too short for it

const formatPauseDuration = (minutes: number): string => {
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

interface Stop {
  assignmentId: number;
  index: number; // 1-based
  type: 'start' | 'end';
  lat: number;
  lng: number;
  label: string;
}

interface Segment {
  assignmentId: number;
  from: google.maps.LatLngLiteral;
  to: google.maps.LatLngLiteral;
}

// Fallback when GpsPauseMinDurationMinutes isn't set — same default as the backend
// detector (DefaultGpsPauseMinDurationMinutes).
const DEFAULT_MIN_PAUSE_MINUTES = 15;

type IncompletePlanFieldsCarrier =
  | {
      contributingAssignmentIdsByReason?: Record<string, number[]> | null;
    }
  | null
  | undefined;

const getReasonsByAssignmentId = (
  carrier: IncompletePlanFieldsCarrier
): Map<number, DayIncompleteReasonEnum[]> => {
  const map = new Map<number, DayIncompleteReasonEnum[]>();
  const dict = carrier?.contributingAssignmentIdsByReason ?? {};
  (Object.entries(dict) as [string, number[]][]).forEach(([reasonKey, ids]) => {
    const reason = Number(reasonKey) as DayIncompleteReasonEnum;
    ids.forEach((id) => {
      const list = map.get(id) ?? [];
      if (!list.includes(reason)) list.push(reason);
      map.set(id, list);
    });
  });
  return map;
};

interface Props {
  /** Shared booked-assignments payload owned by the parent modal. */
  bookedAssignments: GetBookedAssignmentsForUserAndDateList | null;
  isLoading: boolean;
  userId: number;
  date: Date;
}

const WorkDayMap: React.FC<Props> = ({
  bookedAssignments,
  isLoading,
  userId,
  date,
}) => {
  const { scriptLoaded } = useGoogleMapsContext();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<
    {
      assignmentId: number;
      type: 'start' | 'end';
      marker: google.maps.Marker;
    }[]
  >([]);
  const polylinesRef = useRef<
    { assignmentId: number; line: google.maps.Polyline }[]
  >([]);
  // Actual-GPS-track + pause overlay objects, kept separate from the planned
  // route markers/lines so they can be toggled and rebuilt independently.
  const trackLinesRef = useRef<
    { assignmentId: number; line: google.maps.Polyline; isHitLine?: boolean }[]
  >([]);
  const trackDotsRef = useRef<google.maps.Marker[]>([]);
  const pauseMarkersRef = useRef<
    {
      assignmentId: number | null;
      satisfiesBreak: boolean;
      marker: google.maps.Marker;
    }[]
  >([]);
  const gapMarkersRef = useRef<google.maps.Marker[]>([]);
  // Previous hovered id, so the hover effect only restyles elements that actually
  // change (the newly- and previously-hovered assignment) instead of repainting
  // every marker/line on every hover — which makes unrelated ones flicker.
  const prevHoveredIdRef = useRef<number | null>(null);

  const [hoveredId, setHoveredId] = useRecoilState(hoveredDayAssignmentIdAtom);
  const [showPauses, setShowPauses] = useState(false);

  // Hover state setter that debounces CLEARS only. As the pointer glides across the
  // overlapping track/dots/pause layers it fires rapid mouseout→mouseover pairs;
  // clearing immediately makes everything flicker. Setting an id is instant; clearing
  // waits a beat and is cancelled if any layer re-asserts a hover first.
  const hoverClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverAssignment = useCallback(
    (id: number | null) => {
      if (hoverClearTimer.current) {
        clearTimeout(hoverClearTimer.current);
        hoverClearTimer.current = null;
      }
      if (id == null) {
        hoverClearTimer.current = setTimeout(() => setHoveredId(null), 100);
      } else {
        setHoveredId(id);
      }
    },
    [setHoveredId]
  );
  useEffect(
    () => () => {
      if (hoverClearTimer.current) clearTimeout(hoverClearTimer.current);
    },
    []
  );

  // Same "minimum pause" threshold the backend GPS-pause detector uses
  // (GpsPauseMinDurationMinutes) — reused as the minimum gap-between-assignments
  // worth showing, so there's a single configurable notion of "minsta paus".
  const { globalSettings } = useGlobalSettings();
  const minPauseMinutes = useMemo(() => {
    const raw = (globalSettings as Record<string, string>)[
      GlobalSettingEnum.GpsPauseMinDurationMinutes
    ];
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : DEFAULT_MIN_PAUSE_MINUTES;
  }, [globalSettings]);

  // Shared day-driving transform — same hook the work-time widget uses, so the
  // map reads the backend's break/gap computation instead of re-deriving it.
  const dayDrivingTimes = useDayDrivingTimes({
    bookedAssignments: bookedAssignments ?? undefined,
    userId,
  });

  const gpsPausesCall = useAssignmentGetGpsPausesForUserAndDate(
    { userId, date },
    {
      query: {
        enabled: showPauses && userId !== undefined,
        refetchOnWindowFocus: false,
      },
    }
  );
  const pauses = useMemo<GpsPause[]>(
    () => gpsPausesCall.data?.pauses ?? [],
    [gpsPausesCall.data?.pauses]
  );
  // Group the day's track points into one path per assignment, preserving
  // chronological order within each assignment. Drawing one line per assignment
  // (instead of a single day-long line) avoids the criss-cross "stökiga" streck
  // that appear when consecutive assignments jump between the same nodes.
  // Every track point has an assignmentId (the backend fetches positions by
  // assignment), so points without one are dropped rather than bucketed.
  const trackByAssignment = useMemo<
    { assignmentId: number; path: google.maps.LatLngLiteral[] }[]
  >(() => {
    const groups = new Map<number, google.maps.LatLngLiteral[]>();
    (gpsPausesCall.data?.track ?? []).forEach((p) => {
      if (p.assignmentId == null) return;
      const path = groups.get(p.assignmentId) ?? [];
      path.push({ lat: p.latitude, lng: p.longitude });
      groups.set(p.assignmentId, path);
    });
    return Array.from(groups.entries()).map(([assignmentId, path]) => ({
      assignmentId,
      path,
    }));
  }, [gpsPausesCall.data?.track]);

  const trackDots = useMemo<google.maps.LatLngLiteral[]>(
    () =>
      (gpsPausesCall.data?.track ?? []).map((p) => ({
        lat: p.latitude,
        lng: p.longitude,
      })),
    [gpsPausesCall.data?.track]
  );

  const sortedAssignments = useMemo<AssignmentViewModel[]>(() => {
    const list = bookedAssignments?.assignments ?? [];
    return [...list].sort((a, b) => {
      const aStart =
        a.assignment.startTime?.getTime() ??
        a.assignment.estimatedStartTime?.getTime() ??
        0;
      const bStart =
        b.assignment.startTime?.getTime() ??
        b.assignment.estimatedStartTime?.getTime() ??
        0;
      return aStart - bStart;
    });
  }, [bookedAssignments?.assignments]);

  const reasonsByAssignmentId = useMemo(
    () =>
      getReasonsByAssignmentId(
        bookedAssignments as unknown as IncompletePlanFieldsCarrier
      ),
    [bookedAssignments]
  );

  // Gaps between consecutive assignments — classified gray/green/yellow by the
  // shared, unit-tested helper (pure logic, no map deps). We don't track the FT's
  // GPS between assignments; this only reads the time gaps + backend break ladder.
  //
  // useDayDrivingTimes returns a fresh array on every render (its context deps make
  // it unstable), which would re-run classifyGaps and, more importantly, give `gaps`
  // a new reference every render — forcing the draw effect to tear down and rebuild
  // every marker on each hover (flicker). So we key the memo on a stable serialization
  // of just the fields classifyGaps reads; `gaps` then keeps its reference until the
  // underlying values actually change.
  const gapInputKey = useMemo(
    () =>
      JSON.stringify(
        dayDrivingTimes.map((e) => {
          const a = e.assignment?.assignment;
          return [
            a?.assignmentID,
            a?.fromLatitude,
            a?.fromLongitude,
            a?.toLatitude,
            a?.toLongitude,
            e.timeFromPreviousEnd,
            e.minRequiredCumulativeBreakMinutes,
            e.hasEnoughBreakTimeUntilAssignmentEnd,
          ];
        })
      ),
    [dayDrivingTimes]
  );
  const gaps = useMemo<Gap[]>(
    () => classifyGaps(dayDrivingTimes, minPauseMinutes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gapInputKey, minPauseMinutes]
  );

  const { stops, segments } = useMemo<{
    stops: Stop[];
    segments: Segment[];
  }>(() => {
    const stopList: Stop[] = [];
    const segmentList: Segment[] = [];
    sortedAssignments.forEach((m, i) => {
      const a = m.assignment;
      const num = i + 1;
      const reg = m.case.registrationNumber;
      if (a.fromLatitude != null && a.fromLongitude != null) {
        stopList.push({
          assignmentId: a.assignmentID,
          index: num,
          type: 'start',
          lat: a.fromLatitude,
          lng: a.fromLongitude,
          label: `${num}. Start ${reg}`,
        });
      }
      if (a.toLatitude != null && a.toLongitude != null) {
        stopList.push({
          assignmentId: a.assignmentID,
          index: num,
          type: 'end',
          lat: a.toLatitude,
          lng: a.toLongitude,
          label: `${num}. Slut ${reg}`,
        });
      }
      if (
        a.fromLatitude != null &&
        a.fromLongitude != null &&
        a.toLatitude != null &&
        a.toLongitude != null
      ) {
        segmentList.push({
          assignmentId: a.assignmentID,
          from: { lat: a.fromLatitude, lng: a.fromLongitude },
          to: { lat: a.toLatitude, lng: a.toLongitude },
        });
      }
    });
    return { stops: stopList, segments: segmentList };
  }, [sortedAssignments]);

  // Init map
  useEffect(() => {
    if (scriptLoaded && mapRef.current && !mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 5,
        center: SWEDEN_CENTER,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
      });
    }
  }, [scriptLoaded]);

  // Build markers + polylines once per stops/segments/reasons change. We
  // intentionally don't depend on hoveredId here — hover state is applied
  // imperatively in a separate effect to avoid map churn on every mouse-over.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];
    polylinesRef.current.forEach((p) => p.line.setMap(null));
    polylinesRef.current = [];

    const bounds = new google.maps.LatLngBounds();

    stops.forEach((stop) => {
      const hasReasons = reasonsByAssignmentId.has(stop.assignmentId);
      const color = hasReasons ? COLOR_BAD : COLOR_OK;
      const marker = new google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        map,
        label: {
          text: String(stop.index),
          color: 'white',
          fontWeight: 'bold',
          fontSize: '12px',
        },
        title: stop.label,
        zIndex: 10,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: stop.type === 'end' ? 'white' : color,
          strokeWeight: stop.type === 'end' ? 3 : 1,
        },
      });
      marker.addListener('mouseover', () => hoverAssignment(stop.assignmentId));
      marker.addListener('mouseout', () => hoverAssignment(null));
      markersRef.current.push({
        assignmentId: stop.assignmentId,
        type: stop.type,
        marker,
      });
      bounds.extend({ lat: stop.lat, lng: stop.lng });
    });

    segments.forEach((seg) => {
      const hasReasons = reasonsByAssignmentId.has(seg.assignmentId);
      const color = hasReasons ? COLOR_BAD : COLOR_OK;
      const line = new google.maps.Polyline({
        path: [seg.from, seg.to],
        strokeColor: color,
        strokeOpacity: 0.7,
        strokeWeight: 3,
        map,
        clickable: true,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 4,
              strokeColor: color,
              fillColor: color,
              fillOpacity: 1,
            },
            offset: '50%',
          },
        ],
      });
      line.addListener('mouseover', () => hoverAssignment(seg.assignmentId));
      line.addListener('mouseout', () => hoverAssignment(null));
      polylinesRef.current.push({ assignmentId: seg.assignmentId, line });
    });

    if (stops.length > 0) {
      setTimeout(() => map.fitBounds(bounds, 60), 0);
    }
  }, [stops, segments, reasonsByAssignmentId, hoverAssignment]);

  // Draw the actual driven GPS track (one line per assignment + per-point dots)
  // and detected pause markers. Independent of the planned-route effect above so
  // toggling the layer never churns the planned markers.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    trackLinesRef.current.forEach((entry) => entry?.line?.setMap(null));
    trackLinesRef.current = [];
    trackDotsRef.current.forEach((d) => d.setMap(null));
    trackDotsRef.current = [];
    pauseMarkersRef.current.forEach((entry) => entry?.marker?.setMap(null));
    pauseMarkersRef.current = [];
    gapMarkersRef.current.forEach((m) => m.setMap(null));
    gapMarkersRef.current = [];

    if (!showPauses) return;

    trackByAssignment.forEach(({ assignmentId, path }) => {
      if (path.length < 2) return;
      const line = new google.maps.Polyline({
        path,
        strokeColor: COLOR_TRACK,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map,
        zIndex: 5,
        clickable: false,
      });
      trackLinesRef.current.push({ assignmentId, line });

      // A wide, fully transparent line on top carries the hover — a thin 3px line is
      // hard to hit and the dots/markers above intercept the pointer. This makes the
      // track easy to hover and ties it into the same hover state as the planned
      // route and the assignment list. The width is in pixels (constant on screen),
      // so it stays an easy target regardless of map zoom; a generous 28 px keeps it
      // hittable even when the track is short on screen when zoomed out.
      const hitLine = new google.maps.Polyline({
        path,
        strokeColor: COLOR_TRACK,
        strokeOpacity: 0, // invisible — just a hover target
        strokeWeight: 28,
        map,
        zIndex: 9, // above the track line (5) and dots (6) so it reliably catches the pointer
        clickable: true,
      });
      hitLine.addListener('mouseover', () => hoverAssignment(assignmentId));
      hitLine.addListener('mouseout', () => hoverAssignment(null));
      trackLinesRef.current.push({
        assignmentId,
        line: hitLine,
        isHitLine: true,
      });
    });

    // Small grey dot per registered GPS fix so the raw sampling is visible.
    trackDots.forEach((pos) => {
      trackDotsRef.current.push(
        new google.maps.Marker({
          position: pos,
          map,
          clickable: false,
          zIndex: 6,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 2.5,
            fillColor: COLOR_TRACK,
            fillOpacity: 0.7,
            strokeColor: COLOR_TRACK,
            strokeWeight: 0,
          },
        })
      );
    });

    pauses.forEach((pause) => {
      const duration = formatPauseDuration(pause.durationMinutes);
      const span = `${formatTime(new Date(pause.startTime))}–${formatTime(
        new Date(pause.endTime)
      )}`;
      const satisfiesBreak = pause.satisfiesBreakRequirement === true;
      const title = satisfiesBreak
        ? `Paus ${duration} (${span}) — uppfyller uppdragets satta rasttid`
        : `Paus ${duration} (${span})`;
      // Green (fulfils set break) is always shown so the rest signal is visible at a
      // glance. Neutral pauses are muted grey at rest and light up violet when their
      // assignment is hovered (applied in the hover effect), making attribution clear.
      // zIndex 200+ keeps pauses above the planned stop markers even when a stop is
      // hovered (those go up to 100), so the pause info is never hidden.
      const marker = new google.maps.Marker({
        position: { lat: pause.latitude, lng: pause.longitude },
        map,
        title,
        zIndex: satisfiesBreak ? 201 : 200,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: satisfiesBreak ? COLOR_PAUSE_BREAK : COLOR_PAUSE_MUTED,
          fillOpacity: 0.9,
          strokeColor: 'white',
          strokeWeight: 2,
        },
        label: {
          text: '❚❚',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '9px',
        },
      });
      // Drive hover from the pause itself to ITS assignment. Otherwise, when two
      // tracks run close together, the wide hit-line underneath the pause may belong
      // to the other assignment and highlight the wrong route. Pointing at a pause
      // should always highlight the route that pause belongs to.
      const pauseAssignmentId = pause.assignmentId ?? null;
      if (pauseAssignmentId != null) {
        marker.addListener('mouseover', () =>
          hoverAssignment(pauseAssignmentId)
        );
        marker.addListener('mouseout', () => hoverAssignment(null));
      }
      pauseMarkersRef.current.push({
        assignmentId: pauseAssignmentId,
        satisfiesBreak,
        marker,
      });
    });

    // Gaps between assignments — derived from time, not GPS. Drawn as a filled
    // diamond, OFFSET up-and-right of the point so it doesn't sit on top of the
    // start/end circles (which share the spot when an assignment ends and the next
    // begins at the same address) — otherwise the diamond would swallow their hover.
    // The offset is built into the symbol's anchor; the diamond keeps its own
    // tooltip. Green when the gap reaches the required break, otherwise neutral.
    gaps.forEach((gap) => {
      const duration = formatPauseDuration(gap.durationMinutes);
      let title: string;
      let color: string;
      if (gap.breakStatus === 'covered') {
        title = `Glapp mellan uppdrag: ${duration} — räcker som rast (≥ ${gap.requiredBreakMinutes} min krävs)`;
        color = COLOR_PAUSE_BREAK;
      } else if (gap.breakStatus === 'short') {
        title = `Glapp mellan uppdrag: ${duration} — rast krävs (${gap.requiredBreakMinutes} min) men glappet är för kort`;
        color = COLOR_GAP_SHORT;
      } else {
        title = `Glapp mellan uppdrag: ${duration}`;
        color = COLOR_GAP;
      }
      const marker = new google.maps.Marker({
        position: gap.position,
        map,
        title,
        zIndex: 210, // above the start/end circles (≤100) and pause markers (200–202)
        icon: {
          path: 'M 0 -9 L 9 0 L 0 9 L -9 0 Z', // diamond
          scale: 1,
          fillColor: color,
          fillOpacity: 0.95,
          strokeColor: 'white',
          strokeWeight: 2,
          // Anchor down-left of the glyph so the diamond renders up-and-right of
          // the geo point, clear of the overlapping start/end circles.
          anchor: new google.maps.Point(-18, 18),
        },
      });
      gapMarkersRef.current.push(marker);
    });
  }, [trackByAssignment, trackDots, pauses, gaps, showPauses, hoverAssignment]);

  // Apply hover styling imperatively — never rebuilds the markers/lines. Only the
  // newly- and previously-hovered assignment's elements are restyled; everything else
  // is left untouched so unrelated markers/lines don't repaint (and flicker).
  useEffect(() => {
    const prev = prevHoveredIdRef.current;
    const affected = (assignmentId: number | null) =>
      assignmentId != null &&
      (assignmentId === hoveredId || assignmentId === prev);

    markersRef.current.forEach(({ assignmentId, type, marker }) => {
      if (!affected(assignmentId)) return;
      const isHovered = hoveredId === assignmentId;
      const hasReasons = reasonsByAssignmentId.has(assignmentId);
      const color = isHovered ? COLOR_HOVER : hasReasons ? COLOR_BAD : COLOR_OK;
      marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: isHovered ? 16 : 12,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: type === 'end' ? 'white' : color,
        strokeWeight: type === 'end' ? 3 : 1,
      });
      marker.setZIndex(isHovered ? 100 : 10);
    });
    polylinesRef.current.forEach(({ assignmentId, line }) => {
      if (!affected(assignmentId)) return;
      const isHovered = hoveredId === assignmentId;
      const hasReasons = reasonsByAssignmentId.has(assignmentId);
      const color = isHovered ? COLOR_HOVER : hasReasons ? COLOR_BAD : COLOR_OK;
      line.setOptions({
        strokeColor: color,
        strokeOpacity: isHovered ? 1 : 0.7,
        strokeWeight: isHovered ? 5 : 3,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: isHovered ? 5 : 4,
              strokeColor: color,
              fillColor: color,
              fillOpacity: 1,
            },
            offset: '50%',
          },
        ],
      });
    });
    // The actual GPS track line for the hovered assignment highlights too, so it's
    // clear which driven path belongs to the hovered row/planned segment. Keeps the
    // neutral grey when not hovered so the track stays visually distinct from the
    // planned route.
    trackLinesRef.current.forEach((entry) => {
      if (!entry?.line || entry.isHitLine) return; // hit-lines stay invisible
      if (!affected(entry.assignmentId)) return;
      const isHovered = hoveredId === entry.assignmentId;
      entry.line.setOptions({
        strokeColor: isHovered ? COLOR_TRACK_HOVER : COLOR_TRACK,
        strokeOpacity: isHovered ? 1 : 0.8,
        strokeWeight: isHovered ? 5 : 3,
        zIndex: isHovered ? 7 : 5,
      });
    });
    // Pause markers: green (fulfils set break) is always green so the signal stays
    // visible; neutral pauses are muted grey at rest and turn violet for the hovered
    // assignment, making it clear which pauses belong to it. Hovered pauses enlarge.
    pauseMarkersRef.current.forEach((entry) => {
      if (!entry?.marker) return;
      if (!affected(entry.assignmentId)) return;
      const isHovered =
        entry.assignmentId != null && hoveredId === entry.assignmentId;
      const color = entry.satisfiesBreak
        ? COLOR_PAUSE_BREAK
        : isHovered
          ? COLOR_PAUSE
          : COLOR_PAUSE_MUTED;
      entry.marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: isHovered ? 12 : 9,
        fillColor: color,
        fillOpacity: 0.9,
        strokeColor: 'white',
        strokeWeight: 2,
      });
      entry.marker.setZIndex(
        isHovered ? 202 : entry.satisfiesBreak ? 201 : 200
      );
    });

    prevHoveredIdRef.current = hoveredId;
  }, [hoveredId, reasonsByAssignmentId]);

  const plannedCount = useMemo(
    () =>
      sortedAssignments.filter(
        (a) => a.assignment.assignmentStatusID === AssignmentStatusEnum.Planned
      ).length,
    [sortedAssignments]
  );

  const pauseSummary = useMemo(() => {
    if (gpsPausesCall.isLoading) return 'Hämtar GPS-pauser…';
    if (pauses.length === 0) return 'Inga GPS-pauser detekterade.';
    const totalMin = pauses.reduce((sum, p) => sum + p.durationMinutes, 0);
    return `${pauses.length} paus${pauses.length === 1 ? '' : 'er'} längs vägen (totalt ${formatPauseDuration(totalMin)}).`;
  }, [gpsPausesCall.isLoading, pauses]);

  return (
    <Wrapper>
      <Header>Karta</Header>
      <Legend>
        {isLoading
          ? 'Laddar karta…'
          : sortedAssignments.length === 0
            ? 'Inga uppdrag att visa.'
            : `${sortedAssignments.length} uppdrag, varav ${plannedCount} planerade. Röda markörer = bidrar till ofullständig dagsplanering. Hovra för att markera motsvarande rad.`}
      </Legend>
      <LayerLegend>
        <Checkbox
          checked={showPauses}
          onChange={(e) => setShowPauses(e.currentTarget.checked)}
        >
          <span>Visa faktiskt GPS-spår &amp; pauser</span>
        </Checkbox>
        {showPauses && (
          <>
            <LegendKey>
              <LegendItem>
                <Swatch color={COLOR_TRACK}>━</Swatch> GPS-spår
              </LegendItem>
              <LegendItem>
                <Swatch color={COLOR_PAUSE_MUTED}>❚❚</Swatch> stillastående
              </LegendItem>
              <LegendItem>
                <Swatch color={COLOR_PAUSE_BREAK}>❚❚</Swatch> uppfyller satt
                rasttid
              </LegendItem>
            </LegendKey>
            <LegendKey>
              <span>Glapp mellan uppdrag:</span>
              <LegendItem>
                <Swatch color={COLOR_GAP}>◇</Swatch> ingen rast krävs än
              </LegendItem>
              <LegendItem>
                <Swatch color={COLOR_PAUSE_BREAK}>◇</Swatch> räcker som rast
              </LegendItem>
              <LegendItem>
                <Swatch color={COLOR_GAP_SHORT}>◇</Swatch> rast krävs men för
                kort
              </LegendItem>
            </LegendKey>
            {pauseSummary && <div>{pauseSummary}</div>}
          </>
        )}
      </LayerLegend>
      <MapContainer ref={mapRef} />
    </Wrapper>
  );
};

export default WorkDayMap;
